import React from "react";
import {
    Box,
    Container,
    Typography,
    Stack,
    useTheme,
    Paper,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const journeySteps = [
    {
        step: "01",
        title: "Master the Fundamentals",
        desc: "Start with HTML, CSS, and modern JavaScript to build a solid foundation.",
        icon: <CodeIcon sx={{ fontSize: 32 }} />,
        color: "#e50914"
    },
    {
        step: "02",
        title: "Project-First Approach",
        desc: "Apply your knowledge by building real-world clone projects and web apps.",
        icon: <SchoolIcon sx={{ fontSize: 32 }} />,
        color: "#6a0dad"
    },
    {
        step: "03",
        title: "Advanced Specialization",
        desc: "Deep dive into Backend (FastAPI), Databases, and Agentic AI workflows.",
        icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
        color: "#00e676"
    },
    {
        step: "04",
        title: "Industry Readiness",
        desc: "Polish your portfolio, master security protocols, and get job-ready.",
        icon: <EmojiEventsIcon sx={{ fontSize: 32 }} />,
        color: "#A380FF"
    },
];

const LearningJourney = ({ colors, isDark }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                py: { xs: 8, md: 15 },
                position: "relative",
                background: isDark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)",
                overflow: "hidden"
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: "center", mb: 10 }}>
                    <Typography
                        variant="h2"
                        fontWeight={900}
                        sx={{
                            mb: 3,
                            fontSize: { xs: "2.5rem", md: "3.5rem" },
                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Your Learning Journey
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                            maxWidth: 700,
                            mx: "auto",
                            fontWeight: 400
                        }}
                    >
                        A meticulously crafted path to take you from a curious beginner to a professional full-stack developer.
                    </Typography>
                </Box>

                <Box sx={{ position: "relative" }}>
                    {/* 🔗 Horizontal Connector Line (Desktop Only) */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50px",
                            left: "10%",
                            right: "10%",
                            height: "2px",
                            background: `linear-gradient(90deg, transparent 0%, ${colors.primary}40 20%, ${colors.secondary}40 80%, transparent 100%)`,
                            display: { xs: "none", md: "block" },
                            zIndex: 0
                        }}
                    />

                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={{ xs: 6, md: 3 }}
                        justifyContent="space-between"
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        {journeySteps.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    textAlign: "center",
                                    px: 2
                                }}
                            >
                                {/* 🎯 Step Circle */}
                                <Box
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        borderRadius: "35% 65% 70% 30% / 30% 30% 70% 70%",
                                        bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#fff",
                                        border: `2px solid ${item.color}40`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mb: 4,
                                        position: "relative",
                                        transition: "all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                                        boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.3)" : "0 10px 30px rgba(0,0,0,0.05)",
                                        "&:hover": {
                                            transform: "translateY(-10px) rotate(5deg)",
                                            borderColor: item.color,
                                            borderRadius: "50%",
                                            boxShadow: `0 20px 40px ${item.color}30`,
                                            "& .step-num": {
                                                transform: "translate(-50%, -50%) scale(1.1)",
                                                color: item.color
                                            }
                                        }
                                    }}
                                >
                                    <Box sx={{ color: item.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {item.icon}
                                    </Box>

                                    {/* Floating Step Number */}
                                    <Typography
                                        className="step-num"
                                        variant="h4"
                                        fontWeight={900}
                                        sx={{
                                            position: "absolute",
                                            top: "0",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                                            fontSize: "4rem",
                                            zIndex: -1,
                                            transition: "all 0.3s ease",
                                            pointerEvents: "none"
                                        }}
                                    >
                                        {item.step}
                                    </Typography>
                                </Box>

                                <Typography variant="h6" fontWeight={800} sx={{ mb: 2, letterSpacing: "-0.01em" }}>
                                    {item.title}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        lineHeight: 1.8,
                                        fontSize: "0.95rem",
                                        maxWidth: 250
                                    }}
                                >
                                    {item.desc}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                {/* 🎬 Bottom Action Hint */}
                <Box sx={{ mt: 10, textAlign: "center" }}>
                    <Paper
                        elevation={0}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 2,
                            px: 4,
                            py: 2,
                            borderRadius: "100px",
                            bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <AutoAwesomeIcon sx={{ color: colors.primary, fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight={700}>
                            Your destiny is one click away. Are you ready?
                        </Typography>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};

export default LearningJourney;
